import React from 'react';

import { css, keyframes } from '@compiled/react';

import Avatar from '@atlaskit/avatar';
import TaskIcon from '@atlaskit/icon/core/task';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import type { IssueTypeIconName } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';
import { IssueTypeIcon } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/assets/issue-type-svgs';

import modernAvatar from './assets/modern-avatar.png';

type AgentAvatarEntry = { avatar: React.ReactNode; delay: string };

type BoardViewProps = {
	columnNames?: string[];
	issueTypesIcons?: IssueTypeIconName[];
	/** Agent avatar entries to show on cards — first 3 go to TO DO, 4th to IN PROGRESS */
	agentAvatars?: AgentAvatarEntry[];
	isInviteStep?: boolean;
};

/**
 * Kanban board illustration for the JFind onboard illustration.
 * Renders 3 scrollable columns with placeholder issue cards.
 * Column names default to To do / In progress / Complete but can be
 * overridden to reflect the user's work status elections.
 */
export const JfindIllustrationBoardView = ({
	columnNames = ['To do', 'In progress', 'Complete'],
	issueTypesIcons,
	agentAvatars,
	isInviteStep = false,
}: BoardViewProps) => {
	const boardRef = React.useRef<HTMLDivElement>(null);
	const columnRefs = React.useRef<(HTMLDivElement | null)[]>([]);
	const prevColumnNamesRef = React.useRef<string[]>(columnNames);

	React.useEffect(() => {
		const prev = prevColumnNamesRef.current;
		const board = boardRef.current;

		if (!board) {
			prevColumnNamesRef.current = columnNames;
			return;
		}

		if (columnNames.length > prev.length) {
			// New column added — scroll to end
			board.scrollTo({ left: board.scrollWidth, behavior: 'smooth' });
		} else {
			// Check if a column was renamed — find the first changed index
			const changedIndex = columnNames.findIndex((name, i) => name !== prev[i]);
			if (changedIndex !== -1) {
				const columnEl = columnRefs.current[changedIndex];
				if (columnEl) {
					// Scroll only the board wrapper horizontally — avoids scrollIntoView
					// bubbling up and scrolling the modal container vertically
					board.scrollTo({ left: columnEl.offsetLeft, behavior: 'smooth' });
				}
			}
		}

		prevColumnNamesRef.current = columnNames;
	}, [columnNames]);

	return (
		<div
			css={[boardScrollWrapperStyles, isInviteStep && boardScrollWrapperInviteStyles]}
			ref={boardRef}
		>
			<Flex xcss={boardStyles} gap="space.150">
				{columnNames.map((name, colIndex) => (
					<Box
						key={colIndex}
						xcss={columnStyles}
						ref={(el: HTMLDivElement | null) => {
							columnRefs.current[colIndex] = el;
						}}
					>
						<div css={columnHeaderStyles}>{name.trim()}</div>
						<Flex xcss={columnScrollableStyles} direction="column" gap="space.100">
							{agentAvatars !== null && agentAvatars !== undefined
								? colIndex === 0
									? [0, 1, 2].map((cardIndex) => (
											<JfindIllustrationIssueCard
												key={cardIndex}
												agentAvatar={agentAvatars[cardIndex]?.avatar}
												agentAvatarDelay={agentAvatars[cardIndex]?.delay}
											/>
										))
									: colIndex === 1
										? [
												<JfindIllustrationIssueCard
													key="in-progress"
													agentAvatar={agentAvatars[3]?.avatar}
													agentAvatarDelay={agentAvatars[3]?.delay}
												/>,
											]
										: [<JfindIllustrationIssueCard key="completed" />]
								: colIndex === 0
									? issueTypesIcons && issueTypesIcons.length > 0
										? issueTypesIcons.map((icon) => (
												<div key={icon} css={cardFadeInCss}>
													<JfindIllustrationIssueCard issueTypeIcon={icon} />
												</div>
											))
										: [
												<div key="default" css={cardFadeInCss}>
													<JfindIllustrationIssueCard />
												</div>,
											]
									: [
											<JfindIllustrationIssueCard key="s1" />,
											<JfindIllustrationIssueCard key="s2" />,
											<JfindIllustrationIssueCard key="s3" />,
										]}
						</Flex>
					</Box>
				))}
			</Flex>
		</div>
	);
};

const avatarPopInKeyframes = keyframes({
	from: { opacity: 0, transform: 'scale(0.5)' },
	to: { opacity: 1, transform: 'scale(1)' },
});

const avatarPopInCss = css({
	opacity: 0,
	animationName: avatarPopInKeyframes,
	animationDuration: '250ms',
	animationFillMode: 'forwards',
	animationTimingFunction: 'ease-out',
});

type IssueCardProps = {
	issueTypeIcon?: IssueTypeIconName;
	agentAvatar?: React.ReactNode;
	agentAvatarDelay?: string;
};

/**
 * A single placeholder issue card for the kanban board illustration.
 */
export const JfindIllustrationIssueCard = ({
	issueTypeIcon,
	agentAvatar,
	agentAvatarDelay,
}: IssueCardProps) => (
	<Box xcss={issueCardStyles}>
		{issueTypeIcon ? (
			<div css={cardTitleTextStyles}>
				{issueTypeIcon.charAt(0).toUpperCase() + issueTypeIcon.slice(1)}
			</div>
		) : (
			<Skeleton width={122} height={7} borderRadius={3} />
		)}
		<Flex xcss={cardFooterStyles} alignItems="center" gap="space.050">
			{issueTypeIcon ? (
				<IssueTypeIcon type={issueTypeIcon} />
			) : (
				<TaskIcon label="" color={token('color.icon.disabled')} />
			)}
			<Skeleton width={48} height={7} borderRadius={3} />
			<Box xcss={cardAvatarSpacerStyles} />
			{agentAvatar ? (
				<div
					key={String(agentAvatar)}
					css={avatarPopInCss}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ animationDelay: agentAvatarDelay }}
				>
					{agentAvatar}
				</div>
			) : (
				<Avatar size="xsmall" src={modernAvatar} />
			)}
		</Flex>
	</Box>
);

const boardScrollWrapperStyles = css({
	overflowX: 'auto',
	overflowY: 'hidden',
	flex: 1,
	display: 'flex',
});

const boardScrollWrapperInviteStyles = css({
	overflowX: 'hidden',
});

const boardStyles = xcss({
	paddingInlineStart: 'space.400',
	paddingBlockStart: 'space.0',
	flex: 1,
	position: 'relative',
});

const columnStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
	borderRadius: 'border.radius.100',
	width: '195px',
	flexShrink: 0,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	position: 'relative',
});

const columnHeaderStyles = css({
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.050'),
	paddingInline: token('space.100'),
	minHeight: '1em',
	fontSize: '14px',
	fontWeight: 700,
	color: token('color.text.subtle'),
	textTransform: 'uppercase',
	lineHeight: '16px',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const columnScrollableStyles = xcss({
	padding: 'space.100',
	overflowY: 'auto',
	overflowX: 'hidden',
	flex: 1,
});

const issueCardStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderRadius: 'border.radius.100',
	padding: 'space.150',
	boxShadow: 'elevation.shadow.raised',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.250',
	flexShrink: 0,
});

const cardFooterStyles = xcss({
	height: '16px',
	flexShrink: 0,
});

const cardAvatarSpacerStyles = xcss({
	flex: 1,
});

const cardFadeSlideInKeyframes = keyframes({
	from: { opacity: 0, transform: 'translateY(-6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const cardFadeInCss = css({
	animationName: cardFadeSlideInKeyframes,
	animationDuration: '200ms',
	animationFillMode: 'forwards',
	animationTimingFunction: 'ease-out',
});

const cardTitleTextStyles = css({
	font: token('font.body.small'),
	fontWeight: token('font.weight.medium'),
	color: wacToken('color.neutral.light800'),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});
